import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Progress from './shared/Progress';
import Email from './Email';
import Calendar from './Calendar';
import { createContext } from 'react';
import { ClientUpdate } from './clientUpdate/ClientUpdate';
import { Login } from './login/Login';
import { CalendarSyncDisabled } from './calendarSyncDisabled/CalendarSyncDisabled';
import { NonCalendarUser } from './nonCalendarUser/NonCalendarUser';
import { InitializationError } from './InitializationError';
import SaveViewSkeleton from './shared/Skeletons/SaveViewSkeleton';
import { Footer } from './footer/Footer';
import './App.scss';
import { resyncComplete, resyncEvents } from '../../api/emailsApi/calendar';
import { setResyncJobId } from '../actions/calendar';
import { changeAlertText } from '../actions';

export const UserContext = createContext({});

const App = () => {
  const isOfficeInitialized = useSelector((state) => state.app.isOfficeInitialized);
  const itemType = useSelector((state) => state.app.itemType);
  const initializationError = useSelector((state) => state.app.initializationError);
  const authFailure = useSelector((state) => state.auth.authenticationFailure);
  const forcedUserLogOut = useSelector((state) => state.auth.forcedUserLogOut);
  const loginFailed = useSelector((state) => state.auth.loginFailed);
  const requestingLogin = useSelector((state) => state.auth.requestingLogin);
  const isUpdateAvailable = useSelector((state) => state.auth.updateAvailable);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const syncDisabled = useSelector((state) => state.calendar.syncDisabled);
  const isCalendarUser = useSelector((state) => state.calendar.isCalendarUser);
  const user = useSelector((state) => state.auth.user);
  const calendarSyncPermissions = useSelector((state) => state.auth.user?.calendarSyncPermissions);
  const resyncJobId = useSelector((state) => state.calendar.resyncJobId);
  const ownerEmail = useSelector((state) => state.mail.ownerEmail);
  const dispatch = useDispatch();

  const handleResyncEventComplete = async (interval) => {
    const response = await resyncComplete(ownerEmail, resyncJobId);
    if (response?.data?.status === 'success') {
      dispatch(setResyncJobId(null));
      clearInterval(interval);
    } else if (response?.data?.status === 'failed') {
      const isIncompatiblePackage = response?.data?.errorMessage === 'Salesforce resync endpoint not found, incompatible package version';
      dispatch(setResyncJobId(null));
      clearInterval(interval);
      if (!isIncompatiblePackage) {
        const alertText =
        <>
        Calendar event re-sync failed to sync.
          <button
            onClick={handleResyncEventRetry}
            className="alert-try-again-btn"
          >
          Try Again
          </button>
        </>;
        dispatch(changeAlertText(alertText, 'error'));
      }
    }
  };

  const handleResyncEventRetry = async () => {
    dispatch(setResyncJobId(null));
    dispatch(changeAlertText(''));
    try {
      const response = await resyncEvents(ownerEmail);
      dispatch(setResyncJobId(response.data.jobId));
    } catch (e) {
      console.error(e);
      dispatch(changeAlertText('Error starting calendar resync, refresh add-in to try again', 'error'));
    }
  };

  // check to see if job is complete every 5 seconds
  React.useEffect(() => {
    if (!resyncJobId) {
      return;
    }

    const interval = setInterval(() => handleResyncEventComplete(interval), 5000);

    return () => clearInterval(interval);
  }, [resyncJobId]);

  if (!isOfficeInitialized) {
    return (<Progress child={ <SaveViewSkeleton /> } />);
  }

  const isInitializationError = loginFailed || (initializationError && !authFailure && !requestingLogin);
  const isUserLoggedIn = isAuthenticated && !isInitializationError && !authFailure && !forcedUserLogOut;

  const renderLoggedInUser = () => {
    if (itemType === 'message') {
      return <Email />;
    } else if (itemType === 'appointment') {
      if (syncDisabled) {
        return <CalendarSyncDisabled />;
      } else if (!isCalendarUser || (!calendarSyncPermissions?.Outlook_Selective_Sync && !calendarSyncPermissions?.Docrio_Calendar_Sync && calendarSyncPermissions?.Litify_Sync)) {
        return <NonCalendarUser />;
      } else {
        return <Calendar />;
      }
    } else {
      return <InitializationError />;
    }
  };
  return (
    <div className='outlook-container fullHeight flex1 flexColumn'>
      <div className='app'>

        {/* Displays Email, Calendar or error based off of the itemType variable */}
        { isUserLoggedIn && renderLoggedInUser() }
        { (!isAuthenticated || isInitializationError || authFailure || forcedUserLogOut) && (
          <div className='app-container'>
            <div className='main-container'>
              {/* If no itemType is found check for errors and updates */}
              {/* (isInitializationError && !isUpdateAvailable && !forcedUserLogOut) */}
              { (isInitializationError && !isUpdateAvailable && !forcedUserLogOut) && <InitializationError /> }
              { isUpdateAvailable && <ClientUpdate /> }
              {/* if the user is not logged in or the login has failed */}
              { ((authFailure && !loginFailed) || forcedUserLogOut) && <Login/> }
            </div>
          </div>
        )}

        {/* Footer */}
        <div className='app-footer-container'>
          <UserContext.Provider value={user}>
            <Footer />
          </UserContext.Provider>
        </div>
      </div>

    </div>
  );
};

export default App;
